import React, { useEffect, useMemo, useState } from "react";
import { ExternalLink, Trophy, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { EuStars } from "@/components/common/EuStars";
import { fileSrc } from "@/components/mod/ModAchievements";

const TX = {
  hu: { kicker: "Eredményeink", title: "Proud to be European", sub: "Proud to serve the European Nations", intro: "Az alábbiakban a digitális szolgáltatásokról szóló rendelet (DSA) mentén elért legfontosabb eredményeinket mutatjuk be — a biztonságosabb online térért Európa polgárai számára.", all: "Összes", empty: "Hamarosan itt jelennek meg eredményeink.", more: "Részletek" },
  en: { kicker: "Our results", title: "Proud to be European", sub: "Proud to serve the European Nations", intro: "Below are the most significant results we have achieved under the Digital Services Act (DSA) — for a safer online space for the citizens of Europe.", all: "All", empty: "Our results will appear here soon.", more: "Details" },
  de: { kicker: "Unsere Ergebnisse", title: "Proud to be European", sub: "Proud to serve the European Nations", intro: "Hier stellen wir unsere wichtigsten Ergebnisse im Rahmen des Gesetzes über digitale Dienste (DSA) vor — für einen sichereren digitalen Raum für Europas Bürgerinnen und Bürger.", all: "Alle", empty: "Unsere Ergebnisse erscheinen hier in Kürze.", more: "Details" },
  fr: { kicker: "Nos résultats", title: "Proud to be European", sub: "Proud to serve the European Nations", intro: "Voici nos résultats les plus importants obtenus dans le cadre du règlement sur les services numériques (DSA) — pour un espace en ligne plus sûr pour les citoyens européens.", all: "Tous", empty: "Nos résultats apparaîtront bientôt ici.", more: "Détails" },
  cs: { kicker: "Naše výsledky", title: "Proud to be European", sub: "Proud to serve the European Nations", intro: "Níže uvádíme naše nejvýznamnější výsledky dosažené v rámci nařízení o digitálních službách (DSA) — pro bezpečnější online prostor pro občany Evropy.", all: "Vše", empty: "Naše výsledky se zde brzy objeví.", more: "Podrobnosti" },
};

function AchievementCard({ a, more, i }) {
  return (
    <article data-testid={`result-card-${a.id}`} style={{ animationDelay: `${i * 70}ms` }}
      className="group flex animate-in fade-in slide-in-from-bottom-2 flex-col overflow-hidden rounded-2xl border border-zinc-800/70 bg-[#0f1220] fill-mode-both duration-500 transition-colors hover:border-[#FFCC00]/40">
      {a.image_url && <img src={fileSrc(a.image_url)} alt={a.title} className="h-44 w-full object-cover" />}
      <div className="flex flex-1 flex-col p-6">
        <div className="flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider">
          <span className="rounded-full bg-[#003399]/40 px-2.5 py-1 text-blue-200">{a.platform}</span>
          {a.date && <span className="text-zinc-500">{a.date}</span>}
        </div>
        {a.metric_value && (
          <div className="mt-5" data-testid={`result-metric-${a.id}`}>
            <div className="text-4xl font-extrabold tracking-tight text-[#FFCC00]">{a.metric_value}</div>
            {a.metric_label && <div className="mt-1 text-[12px] font-medium uppercase tracking-wider text-zinc-400">{a.metric_label}</div>}
          </div>
        )}
        <h3 className="mt-4 text-[17px] font-semibold leading-snug text-zinc-50">{a.title}</h3>
        {a.description && <p className="mt-2 whitespace-pre-wrap text-[14px] leading-relaxed text-zinc-400">{a.description}</p>}
        {a.link && (
          <a href={a.link} target="_blank" rel="noreferrer" data-testid={`result-link-${a.id}`} className="mt-auto inline-flex items-center gap-1.5 pt-5 text-[13px] font-medium text-blue-400 hover:text-blue-300">
            {more} <ExternalLink size={13} />
          </a>
        )}
      </div>
    </article>
  );
}

export default function ResultsPage() {
  const { lang } = useLang();
  const t = TX[lang] || TX.en;
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    api.get("/achievements").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);

  const platforms = useMemo(() => {
    const ps = [...new Set((items || []).map((a) => a.platform))];
    return ps.sort((a, b) => (a.toLowerCase() === "roblox" ? -1 : b.toLowerCase() === "roblox" ? 1 : a.localeCompare(b)));
  }, [items]);
  const shown = (items || []).filter((a) => !filter || a.platform === filter);

  return (
    <div className="flex min-h-screen flex-col bg-zinc-950 text-zinc-50">
      <SiteHeader />
      <section className="relative overflow-hidden border-b border-zinc-800/70 bg-[#001a4d]">
        <div className="pointer-events-none absolute -right-24 -top-24 opacity-20"><EuStars size={520} /></div>
        <div className="relative mx-auto max-w-6xl px-5 py-20 sm:py-24">
          <div className="flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.2em] text-[#FFCC00]"><Trophy size={14} /> {t.kicker}</div>
          <h1 className="mt-5 text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl" data-testid="results-title">{t.title}</h1>
          <p className="mt-3 text-base font-semibold uppercase tracking-[0.18em] text-[#FFCC00] md:text-lg" data-testid="results-subtitle">{t.sub}</p>
          <p className="mt-6 max-w-2xl text-[15px] leading-relaxed text-blue-100/80">{t.intro}</p>
        </div>
        <div className="h-1 w-full bg-[#FFCC00]" />
      </section>

      <main className="mx-auto w-full max-w-6xl flex-1 px-5 py-14">
        {platforms.length > 1 && (
          <div className="mb-8 flex flex-wrap gap-2" data-testid="results-filters">
            {["", ...platforms].map((p) => (
              <button key={p || "all"} data-testid={`results-filter-${p || "all"}`} onClick={() => setFilter(p)}
                className={`rounded-full px-4 py-1.5 text-[13px] font-medium transition-colors ${filter === p ? "bg-[#FFCC00] text-zinc-950" : "border border-zinc-800 text-zinc-400 hover:text-zinc-100"}`}>{p || t.all}</button>
            ))}
          </div>
        )}
        {items === null ? (
          <div className="grid h-40 place-items-center"><Loader2 className="animate-spin text-zinc-600" /></div>
        ) : shown.length === 0 ? (
          <div className="rounded-2xl border border-zinc-800/70 bg-[#111113] p-10 text-center text-[14px] text-zinc-500" data-testid="results-empty">{t.empty}</div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="results-grid">
            {shown.map((a, i) => <AchievementCard key={a.id} a={a} more={t.more} i={i} />)}
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
